import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import { AITranslationModel } from '../model/ai-translation.model';
import type { AITranslationState } from '../type/ai-translation.type';
import { CheckSensitiveWordDTO, PutTranslationTextDTO } from './ai-translation.repository.interface';

export const TranslationResultService = Symbol('TranslationResultService');

export interface TranslationResultService {
    updateOriginal: (newTranslation: string, idx: number) => Promise<void>;
    updateTranslation: (newTranslation: string, idx: number, needCheck: boolean) => Promise<void>;
}

@injectable()
export class TranslationResultServiceImpl implements TranslationResultService {
    private _onStatesChangeEmitter = new Emitter<AITranslationState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AITranslationModel)
        private _aiTranslationModel: AITranslationModel,
        @inject(CheckSensitiveWordDTO)
        private _checkSensitiveWordDTO: CheckSensitiveWordDTO,
        @inject(PutTranslationTextDTO)
        private _putTranslationTextDTO: PutTranslationTextDTO,
    ) {

        this._aiTranslationModel.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getAITranslationModelData());
    }

    public getAITranslationModelData() {
        return this._aiTranslationModel.modelData;
    }

    private _setAITranslationModelData(value: Partial<AITranslationState>) {
        this._aiTranslationModel.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public async updateOriginal(newOriginal: string, idx: number): Promise<void> {
        const { result } = this.getAITranslationModelData();
        result[idx].original = newOriginal;
        this._setAITranslationModelData({ result });
    }

    public async updateTranslation(newTranslation: string, idx: number, needCheck: boolean = true): Promise<void> {
        if (needCheck) {
            const hasSensitiveWord = await this._checkSensitiveWordDTO.fetch(newTranslation);

            if (hasSensitiveWord) return;
        }

        const aiTranslationModel = this.getAITranslationModelData();
        const { result, taskId } = aiTranslationModel;
        result[idx].translation = newTranslation;
        this._setAITranslationModelData({ result });
        this._putTranslationTextDTO.fetch({
            taskId,
            currentStep: 'RESULT',
            originalIndex: idx,
            translationText: newTranslation
        });
    }
}